import { useSmartLinkContext } from '@atlaskit/link-provider';
import { AsyncSelect } from '@databases/atlaskit/Select';
import DatasourcesRenderComponent, {
  type FieldTypeWithOnlyValues,
} from '@databases/field-value';
import { defaultStoreUtils } from '@databases/redux';
import {
  type JiraIssueRef,
  type ResolvedJiraIssueRef,
} from '@databases/types/confluence';
import { type DatabaseFieldType } from '@databases/types/database';
import { addJiraIssuesToList, loadJiraIssue } from '@databases/utils/redux/jira/actions';
import React, {
  type FunctionComponent,
  type CSSProperties,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import { PopoverEdit } from '../PopoverEdit';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection';
import { type EditComponentProps } from '../types';
import { withJiraWorkItemCheck } from '../withJiraWorkItemCheck';

import {
  useResolvedIssueAndHost,
  useIssueRefsAndStatusItems,
  useIssueTransitions,
  useOnSelectTransition,
  buildTransitionPreviewItem,
} from './JiraStatusUtil';
import { type TransitionItem } from './api';
import { messages } from './i18n';

const addDataSectionStyles: CSSProperties = {
  listStyleType: 'none',
  padding: '0',
};

interface TransitionOption {
  label: string;
  value: string; // transition ID
  transition: TransitionItem;
}

const JiraStatusPopover: FunctionComponent<
  EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>
> = ({
  contentId,
  entryId,
  field,
  onFocus,
  onBlur,
  isDefaultValue,
  isFilterValueEdit,
  autoFocus,
  isBulkEdit,
}) => {
  const isRenderedInSidePanel = Boolean(isDefaultValue || isFilterValueEdit);

  usePopoverSoftSelection({
    entryId,
    fieldId: field.id,
    isRenderedInSidePanel,
  });

  useSmartLinkContext();
  const { issueRefs, statusItems } = useIssueRefsAndStatusItems(
    contentId,
    entryId,
    field
  );

  const addDataSection = useMemo(
    () => (
      <ul style={addDataSectionStyles}>
        {issueRefs?.map((issueRef: JiraIssueRef, i: number) => (
          <li key={`${issueRef.key || 'issue'}-${i}`}>
            <SingleJiraStatusEdit
              issueRef={issueRef}
              statusItem={statusItems[i]}
              issueRefs={issueRefs}
              contentId={contentId}
              onFocus={onFocus}
              onBlur={onBlur}
              autoFocus={autoFocus}
            />
          </li>
        ))}
      </ul>
    ),
    [issueRefs, statusItems, contentId, onFocus, onBlur, autoFocus]
  );

  if (!issueRefs?.length || issueRefs.length !== statusItems.length) {
    return null;
  }

  return (
    <PopoverEdit
      isMultiSelect={false}
      hasCurrentData={false}
      addDataSection={addDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
      isBulkEdit={isBulkEdit}
    />
  );
};

const JiraStatusValueEdit = withJiraWorkItemCheck('status', JiraStatusPopover);

export default JiraStatusValueEdit;

interface SingleJiraStatusEditProps
  extends Pick<
    EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
    'onFocus' | 'onBlur' | 'autoFocus' | 'contentId'
  > {
  issueRef: JiraIssueRef;
  statusItem: FieldTypeWithOnlyValues;
  issueRefs: JiraIssueRef[];
}

const SingleJiraStatusEdit: FunctionComponent<SingleJiraStatusEditProps> = ({
  issueRef,
  statusItem,
  onFocus,
  onBlur,
  autoFocus,
  issueRefs,
  contentId,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();

  const [resolvedIssue, , hostname] = useResolvedIssueAndHost(issueRef);

  const shouldPreload = issueRefs?.length === 1;

  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const shouldAutoOpen = shouldPreload && !hasAutoOpened;

  const handleMenuOpen = useCallback(() => {
    if (shouldPreload) {
      setHasAutoOpened(true);
    }
  }, [shouldPreload]);

  const { isLoading, transitions, fetchTransitions, clearTransitions, isMountedRef } =
    useIssueTransitions(issueRef, hostname, shouldPreload);

  const currentValue = useMemo((): TransitionOption | undefined => {
    if (!statusItem?.values?.[0]) {
      return undefined;
    }

    const currentStatus = statusItem.values[0] as {
      id: string;
      style?: { appearance: string };
      text: string;
    };
    return {
      label: currentStatus.text || '',
      value: currentStatus.text || '',
      transition: {
        id: currentStatus.id || '',
        name: currentStatus.text || '',
        appearance: currentStatus.style?.appearance || 'default',
      },
    };
  }, [statusItem]);

  const handleOptimisticUpdate = useCallback(
    (optimisticIssue: ResolvedJiraIssueRef) => {
      dispatch(addJiraIssuesToList([optimisticIssue]));
    },
    [dispatch]
  );

  const handleReloadIssue = useCallback(
    async (issueRef: JiraIssueRef) => {
      await dispatch(loadJiraIssue(issueRef, { columns: ['status'], forceReload: true }));
    },
    [dispatch]
  );

  const { selectTransition, isLoading: isSelecting } = useOnSelectTransition({
    issueRef,
    hostname,
    transitions,
    resolvedIssue,
    isMountedRef,
    clearTransitions,
    currentStatusName: currentValue?.label,
    currentStatusId: currentValue?.transition.id,
    onOptimisticUpdate: handleOptimisticUpdate,
    onReloadIssue: handleReloadIssue,
    contentId,
  });

  const renderTransition = useCallback(
    (t: TransitionItem) => {
      const item = buildTransitionPreviewItem(statusItem, t);
      return <DatasourcesRenderComponent item={item} />;
    },
    [statusItem]
  );

  const loadOptions = useCallback(async (): Promise<TransitionOption[]> => {
    try {
      let allTransitions: TransitionItem[] = [];

      if (!transitions?.length) {
        allTransitions = await fetchTransitions();
      } else {
        allTransitions = transitions;
      }

      // Filter out the current status to prevent duplication
      const currentStatusName = currentValue?.label;
      const filteredTransitions = allTransitions.filter(
        (transition: TransitionItem) => transition.name !== currentStatusName
      );

      return filteredTransitions.map((transition: TransitionItem) => ({
        label: transition.name,
        value: transition.id,
        transition,
      }));
    } catch {
      return [];
    }
  }, [fetchTransitions, transitions, currentValue?.label]);

  const formatOptionLabel = useCallback(
    (option: TransitionOption) => {
      return renderTransition(option.transition);
    },
    [renderTransition]
  );

  return (
    <AsyncSelect<TransitionOption>
      value={currentValue}
      loadOptions={loadOptions}
      onChange={(option) => {
        if (option?.transition) {
          selectTransition(option.transition.id);
        }
      }}
      placeholder={formatMessage(messages['jira.status.transitions.non-final'])}
      formatOptionLabel={formatOptionLabel}
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.label}
      isSearchable={false}
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      isLoading={isLoading || isSelecting}
      noOptionsMessage={() => formatMessage(messages['jira.status.noTransitions'])}
      defaultMenuIsOpen={shouldAutoOpen}
      onMenuOpen={handleMenuOpen}
      styles={{
        menu: (base, props) => ({
          ...base,
          width: props.options.length > 0 ? 'max-content' : 'auto',
          minWidth: '100%',
        }),
      }}
    />
  );
};
